import type { ToolPart } from '../../types';
import { useLayoutStore } from "../../layout/LayoutProvider";

/**
 * Inline renderer for `canvas_write` tool calls.
 *
 * Purpose: a compact clickable chip in the chat stream that lets the user
 * jump back to the canvas view if they closed it. Does NOT auto-open the
 * canvas — that's handled centrally in `App.tsx` via a `state.canvas`
 * useEffect, which is race-free (React can batch TOOL_CALL_* events into
 * one render where this card would mount already in `completed` status,
 * defeating any mount-based auto-open logic).
 */

interface Props {
  part: ToolPart;
}

export function CanvasCard({ part }: Props) {
  const { mainView, openMain } = useLayoutStore();

  const isActive = part.state.status === 'running';
  const isError = part.state.status === 'error';
  const isOpen = mainView?.id === 'canvas';
  const dotClass = isError
    ? 'tool-dot-error'
    : isActive
      ? 'tool-dot-active'
      : 'tool-dot-done';

  const content = (part.state.input?.content as string | undefined) || '';
  const chars = content.length;

  const label = isActive
    ? 'Пишу в холст…'
    : isError
      ? 'Не удалось обновить холст'
      : `Холст обновлён · ${chars} симв.`;

  const handleClick = () => {
    if (!isOpen) openMain('canvas');
  };

  // Trailing affordance — arrow icon when canvas is closed (click to open),
  // check icon when canvas is already open (just a status cue).
  const hint = isOpen ? 'Открыт' : 'Открыть';

  return (
    <div
      className={`tool-step tool-${part.state.status} canvas-card${isOpen ? ' canvas-card--open' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
      title={isOpen ? 'Холст уже открыт справа' : 'Открыть холст'}
    >
      <div className="tool-step-rail">
        <div className={`tool-step-dot ${dotClass}`} />
      </div>
      <div className="tool-step-body canvas-card-body">
        <div className={`tool-step-label ${isActive ? 'tool-active' : ''}`}>
          {label}
        </div>
        {isError && part.state.error && (
          <div className="tool-step-content">
            <pre className="tool-error">{part.state.error}</pre>
          </div>
        )}
      </div>
      {!isError && (
        <div className="canvas-card-cta" aria-hidden>
          <span className="canvas-card-cta-text">{hint}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="M12 5l7 7-7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
